import React, { useState, useContext, useEffect, useMemo } from "react";
import Equipments from "./Equipments";
import DefaultFee from "./DefaultFee";
import { Stack } from "@mui/material";
import CurrencyInput from "./input-elements/CurrencyInput";
import PercentageInput from "./input-elements/PercentageInput";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { EquipmentContext } from "../useContext/context";
import { Form } from "react-bootstrap";
import * as Yup from "yup";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";


const ach_validation = pricingValidation.pricingValidation.ach;

// Add custom validation method
Yup.addMethod(Yup.object, 'requireOneAchCapability', function() {
    return this.test('ach_capabilities', 'At least one ACH capability must be enabled', function(value) {
        return value.ach_by_phone === 'yes' || 
               value.ach_by_web === 'yes' || 
               value.ach_by_preauth === 'yes' || 
               value.ach_credit_transaction === 'yes';
    });
});

// Separate schema just for ACH capabilities
const achCapabilitiesSchema = Yup.object().shape({
    ach_by_phone: Yup.string().oneOf(['yes', 'no']),
    ach_by_web: Yup.string().oneOf(['yes', 'no']),
    ach_by_preauth: Yup.string().oneOf(['yes', 'no']),
    ach_credit_transaction: Yup.string().oneOf(['yes', 'no']),
}).requireOneAchCapability();

const ACHPricing = ({
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError: removeErrorEq,
    isAddOn = false,
    isEditable = true,
}) => {
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);
    const [achDefaults, setAchDefaults] = useState(ach_validation);

    // Update ACH defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.ach) {
            setAchDefaults(pricingDefaults.ach);
        }
    }, [pricingDefaults]);

    // Set default values when component mounts or ACH defaults change
    useEffect(() => {
        const defaultValues = {
            ach_discount_rate: achDefaults.ach_discount_rate?.default || ach_validation.ach_discount_rate.default,
            ach_transaction_fee_per_item: achDefaults.ach_transaction_fee_per_item?.default || ach_validation.ach_transaction_fee_per_item.default,
            ach_statement_fee: achDefaults.ach_statement_fee?.default || ach_validation.ach_statement_fee.default,
            ach_monthly_minimum: achDefaults.ach_monthly_minimum?.default || ach_validation.ach_monthly_minimum.default,
            ach_return_fee: achDefaults.ach_return_fee?.default || ach_validation.ach_return_fee.default,
            ach_noc_fee: achDefaults.ach_noc_fee?.default || ach_validation.ach_noc_fee.default,
            ach_unauthorized_return_fee: achDefaults.ach_unauthorized_return_fee?.default || ach_validation.ach_unauthorized_return_fee.default,
            ach_same_day_fee: achDefaults.ach_same_day_fee?.default || ach_validation.ach_same_day_fee.default,
            ach_bank_validation_fee: achDefaults.ach_bank_validation_fee?.default || ach_validation.ach_bank_validation_fee.default,
            ach_by_phone: "no",
            ach_by_web: "no",
            ach_by_preauth: "no",
            ach_credit_transaction: "no",
            ach_capabilities: "",
        };

        // Only set values that aren't already set in form_data
        Object.entries(defaultValues).forEach(([key, value]) => {
            if (form_data[key] === undefined || form_data[key] === "") {
                setFormDataByKeyValue(key, value);
            }
        });
    }, [achDefaults]); // Re-run when ACH defaults change

    // Create validation schema based on current ACH defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            ach_discount_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_discount_rate?.minimum || ach_validation.ach_discount_rate.minimum,
                    `Please enter a rate of ${(achDefaults.ach_discount_rate?.minimum || ach_validation.ach_discount_rate.minimum).toFixed(2)}% or higher.`,
                ),

            ach_transaction_fee_per_item: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Transaction Fee Per Item is required")
                .min(
                    achDefaults.ach_transaction_fee_per_item?.minimum || ach_validation.ach_transaction_fee_per_item.minimum,
                    `Please enter a fee of $${(achDefaults.ach_transaction_fee_per_item?.minimum || ach_validation.ach_transaction_fee_per_item.minimum).toFixed(2)} or higher.`,
                ),

            ach_statement_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_statement_fee?.minimum || ach_validation.ach_statement_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_statement_fee?.minimum || ach_validation.ach_statement_fee.minimum).toFixed(2)} or higher.`,
                ),

            ach_monthly_minimum: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_monthly_minimum?.minimum || ach_validation.ach_monthly_minimum.minimum,
                    `Please enter a minimum of $${(achDefaults.ach_monthly_minimum?.minimum || ach_validation.ach_monthly_minimum.minimum).toFixed(2)} or higher.`,
                ),

            ach_return_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_return_fee?.minimum || ach_validation.ach_return_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_return_fee?.minimum || ach_validation.ach_return_fee.minimum).toFixed(2)} or higher.`,
                ),

            ach_noc_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_noc_fee?.minimum || ach_validation.ach_noc_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_noc_fee?.minimum || ach_validation.ach_noc_fee.minimum).toFixed(2)} or higher.`,
                ),

            ach_unauthorized_return_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_unauthorized_return_fee?.minimum || ach_validation.ach_unauthorized_return_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_unauthorized_return_fee?.minimum || ach_validation.ach_unauthorized_return_fee.minimum).toFixed(2)} or higher.`,
                ),

            ach_same_day_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_same_day_fee?.minimum || ach_validation.ach_same_day_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_same_day_fee?.minimum || ach_validation.ach_same_day_fee.minimum).toFixed(2)} or higher.`,
                ),

            ach_bank_validation_fee: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .min(
                    achDefaults.ach_bank_validation_fee?.minimum || ach_validation.ach_bank_validation_fee.minimum,
                    `Please enter a fee of $${(achDefaults.ach_bank_validation_fee?.minimum || ach_validation.ach_bank_validation_fee.minimum).toFixed(2)} or higher.`,
                ),
        });
    }, [achDefaults]); // Only recreate when achDefaults change

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);

        // If the changed field is one of the ACH capabilities, validate only those fields
        if (
            [
                "ach_by_phone",
                "ach_by_web",
                "ach_by_preauth",
                "ach_credit_transaction",
            ].includes(e.target.name)
        ) {
            const achCapabilities = {
                ach_by_phone: form_data.ach_by_phone,
                ach_by_web: form_data.ach_by_web,
                ach_by_preauth: form_data.ach_by_preauth,
                ach_credit_transaction: form_data.ach_credit_transaction,
                [e.target.name]: e.target.value,
            };

            achCapabilitiesSchema
                .validate(achCapabilities, { abortEarly: false })
                .then(() => {
                    let validation_errors = { ...errors };
                    delete validation_errors["ach_capabilities"];
                    setErrors(validation_errors);
                })
                .catch((err) => {
                    let validation_errors = { ...errors };
                    validation_errors["ach_capabilities"] = [
                        "At least one ACH capability must be enabled",
                    ];
                    setErrors(validation_errors);
                });
        } else {
            // Original validation for other fields
            validationSchema
                .validateAt(e.target.name, { [e.target.name]: e.target.value })
                .then((e) => {
                    // nothing here
                })
                .catch((err) => {
                    let validation_errors = errors;
                    validation_errors = {
                        ...validation_errors,
                        [e.target.name]: err.errors,
                    };
                    setErrors(validation_errors);
                });
        }
    };

    return (
        <div className="ach-pricing-container">
            <div className="ach-pricing-section">
                <div className="equipment-box ach-equipment-box mt-2r">
                    <p className="box-label ach-box-label">ACH Pricing</p>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100">
                            <PercentageInput
                                label="Discount Rate %"
                                placeholder={
                                    achDefaults.ach_discount_rate?.default
                                }
                                name={"ach_discount_rate"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_discount_rate}
                                errors={errors}
                                softLimit={achDefaults?.ach_discount_rate?.soft_limit}
                            />
                            {errors?.ach_discount_rate &&
                                Array.isArray(errors.ach_discount_rate) &&
                                errors.ach_discount_rate.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Transaction Fee Per Item"
                                placeholder={
                                    achDefaults.ach_transaction_fee_per_item?.default
                                }
                                name={"ach_transaction_fee_per_item"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_transaction_fee_per_item}
                                errors={errors}
                                softLimit={achDefaults?.ach_transaction_fee_per_item?.soft_limit}
                            />
                            {errors?.ach_transaction_fee_per_item &&
                                Array.isArray(
                                    errors.ach_transaction_fee_per_item,
                                ) &&
                                errors.ach_transaction_fee_per_item.map(
                                    (error) => (
                                        <span className="error-message ach-error-message">
                                            {error}
                                        </span>
                                    ),
                                )}
                        </div>
                    </Stack>
                </div>

                <div className="equipment-box ach-equipment-box mt-2r">
                    <p className="box-label ach-box-label">Additional Fees</p>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Statement Fee"
                                placeholder={
                                    achDefaults.ach_statement_fee?.default
                                }
                                name={"ach_statement_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_statement_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_statement_fee?.soft_limit}
                            />
                            {errors?.ach_statement_fee &&
                                Array.isArray(errors.ach_statement_fee) &&
                                errors.ach_statement_fee.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Monthly Minimum"
                                placeholder={
                                    achDefaults.ach_monthly_minimum?.default
                                }
                                name={"ach_monthly_minimum"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_monthly_minimum}
                                errors={errors}
                                softLimit={achDefaults?.ach_monthly_minimum?.soft_limit}
                            />
                            {errors?.ach_monthly_minimum &&
                                Array.isArray(errors.ach_monthly_minimum) &&
                                errors.ach_monthly_minimum.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Return Fee"
                                placeholder={
                                    achDefaults.ach_return_fee?.default
                                }
                                name={"ach_return_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_return_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_return_fee?.soft_limit}
                            />
                            {errors?.ach_return_fee &&
                                Array.isArray(errors.ach_return_fee) &&
                                errors.ach_return_fee.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                    </Stack>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="NOC Fee"
                                placeholder={achDefaults.ach_noc_fee?.default}
                                name={"ach_noc_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_noc_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_noc_fee?.soft_limit}
                            />
                            {errors?.ach_noc_fee &&
                                Array.isArray(errors.ach_noc_fee) &&
                                errors.ach_noc_fee.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Unauthorized Return Fee"
                                placeholder={
                                    achDefaults.ach_unauthorized_return_fee?.default
                                }
                                name={"ach_unauthorized_return_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_unauthorized_return_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_unauthorized_return_fee?.soft_limit}
                            />
                            {errors?.ach_unauthorized_return_fee &&
                                Array.isArray(
                                    errors.ach_unauthorized_return_fee,
                                ) &&
                                errors.ach_unauthorized_return_fee.map(
                                    (error) => (
                                        <span className="error-message ach-error-message">
                                            {error}
                                        </span>
                                    ),
                                )}
                        </div>
                        <div className="w-100 ach-w-100">
                            <CurrencyInput
                                label="Same Day ACH Fee"
                                placeholder={
                                    achDefaults.ach_same_day_fee?.default
                                }
                                name={"ach_same_day_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_same_day_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_same_day_fee?.soft_limit}
                            />
                            {errors?.ach_same_day_fee &&
                                Array.isArray(errors.ach_same_day_fee) &&
                                errors.ach_same_day_fee.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                    </Stack>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100 validation-fee-col">
                            <CurrencyInput
                                label="Per ACH Web Transaction - Bank Account Validation Fee"
                                placeholder={
                                    achDefaults.ach_bank_validation_fee?.default
                                }
                                name={"ach_bank_validation_fee"}
                                type="number"
                                onChange={handleOnChange}
                                value={form_data.ach_bank_validation_fee}
                                errors={errors}
                                softLimit={achDefaults?.ach_bank_validation_fee?.soft_limit}
                            />
                            {errors?.ach_bank_validation_fee &&
                                Array.isArray(errors.ach_bank_validation_fee) &&
                                errors.ach_bank_validation_fee.map((error) => (
                                    <span className="error-message ach-error-message">
                                        {error}
                                    </span>
                                ))}
                        </div>
                        <div className="w-100 ach-w-100 empty-col"></div>
                    </Stack>
                </div>

                <div className="equipment-box ach-equipment-box mt-2r">
                    <p className="box-label ach-box-label">ACH Capabilities</p>
                    {errors?.["ach_capabilities"] && (
                        <span className="error-message ach-error-message">
                            {errors["ach_capabilities"][0]}
                        </span>
                    )}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100">
                            <ToggleSwitchContainer
                                handleChange={(_e, value) =>
                                    handleOnChange({
                                        target: { name: "ach_by_phone", value },
                                    })
                                }
                                value={form_data.ach_by_phone}
                                options={[
                                    { key: "yes", value: "Yes" },
                                    { key: "no", value: "No" },
                                ]}
                                name="ach_by_phone"
                                label="ACH by Phone (TEL)"
                            />
                        </div>
                        <div className="w-100 ach-w-100">
                            <ToggleSwitchContainer
                                handleChange={(_e, value) =>
                                    handleOnChange({
                                        target: { name: "ach_by_web", value },
                                    })
                                }
                                value={form_data.ach_by_web}
                                options={[
                                    { key: "yes", value: "Yes" },
                                    { key: "no", value: "No" },
                                ]}
                                name="ach_by_web"
                                label="ACH by Web (WEB)"
                            />
                        </div>
                    </Stack>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        className="container-col-2 ach-container-col-2 mb-1r mt-2r"
                    >
                        <div className="w-100 ach-w-100">
                            <ToggleSwitchContainer
                                handleChange={(_e, value) =>
                                    handleOnChange({
                                        target: {
                                            name: "ach_by_preauth",
                                            value,
                                        },
                                    })
                                }
                                value={form_data.ach_by_preauth}
                                options={[
                                    { key: "yes", value: "Yes" },
                                    { key: "no", value: "No" },
                                ]}
                                name="ach_by_preauth"
                                label="ACH by Pre-authorized Debit (PPD)"
                            />
                        </div>
                        <div className="w-100 ach-w-100">
                            <ToggleSwitchContainer
                                handleChange={(_e, value) =>
                                    handleOnChange({
                                        target: {
                                            name: "ach_credit_transaction",
                                            value,
                                        },
                                    })
                                }
                                value={form_data.ach_credit_transaction}
                                options={[
                                    { key: "yes", value: "Yes" },
                                    { key: "no", value: "No" },
                                ]}
                                name="ach_credit_transaction"
                                label="Credit Transaction Capacity (PPD)"
                            />
                        </div>
                    </Stack>
                </div>

                {!isAddOn && isEditable && (
                    <Equipments
                        emsEquipments={emsEquipments}
                        setFormDataByKeyValue={setFormDataByKeyValue}
                        form_data={form_data}
                        errors={errors}
                        allEquipments={allEquipments}
                        removeError={removeErrorEq}
                    />
                )}

                <style jsx>{`
                    .ach-pricing-container {
                        margin-top: 6rem;
                    }
                    .ach-equipment-box {
                        padding: 0.5rem 1rem !important;
                        border-radius: 4px;
                    }
                    .ach-box-label {
                        font-weight: 600;
                        margin-bottom: 0.5rem;
                    }
                    .ach-w-100 {
                        width: 100%;
                    }
                    .ach-w-60 {
                        width: 60%;
                    }
                    .ach-d-flex {
                        display: flex;
                    }
                    .ach-justify-content-center {
                        justify-content: center;
                    }
                    .mt-2r {
                        margin-top: 2rem;
                    }
                    .mb-1r {
                        margin-bottom: 1rem;
                    }
                    .mb-4r {
                        margin-bottom: 4rem;
                    }
                    .ach-container-col-2 {
                        display: flex;
                        gap: 1rem;
                        flex-wrap: wrap;
                    }
                    .ach-container-col-2 > div {
                        flex: 1 0 calc(33.333% - 1rem);
                    }
                    .ach-equipment-box:first-child .ach-container-col-2 > div,
                    .ach-equipment-box:last-child .ach-container-col-2 > div {
                        flex: 1 0 calc(50% - 1rem);
                    }
                    .validation-fee-col {
                        flex: 1 0 calc(66.666% - 1rem) !important;
                    }
                    .validation-fee-col .makeStyles-root-5 .MuiFormLabel-root {
                        font-size: 18.5px;
                    }
                    .empty-col {
                        flex: 1 0 calc(33.333% - 1rem) !important;
                    }
                    .ach-error-message {
                        color: #dc3545;
                        font-size: 12px;
                        margin-top: 0.25rem;
                        display: block;
                    }
                    @media (max-width: 767px) {
                        .ach-container-col-2 > div {
                            flex: 1 0 100%;
                        }
                    }

                    /* Add back original classes */
                    input[type="text"],
                    input[type="number"] {
                        min-width: 0;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    .floating-label {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 100%;
                    }
                    .input-control {
                        padding-right: 0.5rem;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default ACHPricing; 
